import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CookieBanner } from "@/components/CookieBanner";
import { ScrollProgress } from "@/components/ScrollProgress";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";
import { PRIMARY_LOCAL_BUSINESS } from "@/lib/localBusinessSchema";

import appCss from "../styles.css?url";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-gradient-brand px-4 py-2 text-sm font-semibold text-brand-foreground"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      {
        httpEquiv: "Content-Security-Policy",
        content: [
          "default-src 'self'",
          "base-uri 'self'",
          "object-src 'none'",
          
          "frame-src 'self' https://www.googletagmanager.com https://*.trustpilot.com https://trustpilot.com https://www.google.com https://maps.google.com https://*.google.com",
          "form-action 'self'",
          "script-src 'self' 'unsafe-inline' data: https://www.googletagmanager.com https://cdn.gpteng.co https://*.lovable.dev https://lovable.dev https://widget.trustpilot.com https://*.trustpilot.com",
          "style-src 'self' 'unsafe-inline'",
          "font-src 'self' data:",
          "img-src 'self' data: blob: https:",
          "connect-src 'self' https: wss:",
          "media-src 'self' https: data:",
          "manifest-src 'self' https://lovable.dev https://*.lovable.dev",
          "upgrade-insecure-requests",
        ].join("; "),
      },
      { title: "Apex AV — Denver Home Automation & AV Installation Company" },
      { name: "description", content: "Denver's trusted home automation and AV company. Custom home theaters, smart home, whole-home audio, lighting & shading across the Front Range." },
      { name: "author", content: "Apex AV" },
      { name: "keywords", content: "home automation Denver, AV installation Denver, smart home Denver, home theater installation Denver, whole-home audio Denver, smart home dealer Denver, custom AV company Denver, lighting control Denver, motorized shades Denver, TV mounting Denver, low voltage prewire Denver, AV company Denver Metro, Front Range home theater installer, Cherry Creek smart home, Castle Rock home automation, Highlands Ranch AV installer, Boulder home theater" },
      { name: "geo.region", content: "US-CO" },
      { name: "geo.placename", content: "Denver" },
      { name: "geo.position", content: "39.7392;-104.9903" },
      { name: "ICBM", content: "39.7392, -104.9903" },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:locale", content: "en_US" },
      { property: "og:site_name", content: "Apex AV" },
      { property: "og:title", content: "Apex AV — Denver Home Automation & AV Installation Company" },
      { property: "og:description", content: "Denver's trusted home automation and AV company. Custom home theaters, smart home, whole-home audio, lighting & shading across the Front Range." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Apex AV — Denver Home Automation & AV Installation Company" },
      { name: "twitter:description", content: "Denver's trusted home automation and AV company. Custom home theaters, smart home, whole-home audio, lighting & shading across the Front Range." },
      { name: "twitter:site", content: "@ApexAV80211" },
      { name: "twitter:creator", content: "@ApexAV80211" },
      { name: "google-site-verification", content: "FgltGyXm_OICQrvQ2QJlKnRolr8BxTVLbJBY_dgr4Og" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            { ...PRIMARY_LOCAL_BUSINESS, image: `https://apexav.com${apexTheaterRichImg.url}` },
            {
              "@type": "WebSite",
              "@id": "https://apexav.com/#website",
              url: "https://apexav.com/",
              name: "Apex AV",
              publisher: { "@id": "https://apexav.com/#organization" },
              inLanguage: "en-US",
            },
          ],
        }),
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://www.googletagmanager.com", crossOrigin: "" } as never,
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/space-grotesk.woff2", crossOrigin: "anonymous" } as never,
      { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/dm-sans.woff2", crossOrigin: "anonymous" } as never,
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/favicon-48.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "sitemap", type: "application/xml", title: "Sitemap", href: "/sitemap.xml" } as never,
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <div className="flex min-h-screen flex-col">
      <ScrollProgress />
      <SiteHeader />
      <main className="flex-1">
        <Breadcrumbs />
        <Outlet />
      </main>
      <SiteFooter />
      <CookieBanner />
    </div>
  );
}
